import React from 'react';
import {AlignItems, JustifyContent} from '@gongxigames/framework/core/fragments/display';
import {IEventManagerWithAsync} from '@gongxigames/framework/core/types';
import {ScreenManager} from '@gongxigames/framework/core/services/screenManager/ScreenManager';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {FlexBoxComponent} from '@gongxigames/framework/core/fragments/react/components/FlexBoxComponent';
import {ScreenParameters} from '@gongxigames/framework/core/fragments/ScreenParameters';
import {lazyInject} from '@gongxigames/framework/core/installer';
import {Store} from '@gongxigames/framework/core';
import {slotStore} from '@gongxigames/framework/slotV2/globals';

type VerticalAlign = 'top' | 'center' | 'bottom';
type HorizontalAlign = 'left' | 'center' | 'right';

type AlignerByScreenProps = {
    verticalAlign?: VerticalAlign;
    horizontalAlign?: HorizontalAlign;
};

type AlignerByScreenState = {
    screenWidth: number;
    screenHeight: number;
};

const VERTICAL_ALIGN: Record<VerticalAlign, AlignItems> = {
    top: 'start',
    center: 'center',
    bottom: 'end'
};

const HORIZONTAL_ALIGN: Record<HorizontalAlign, JustifyContent> = {
    left: 'start',
    center: 'center',
    right: 'end'
};

export class AlignerByScreen extends React.Component<AlignerByScreenProps, AlignerByScreenState> {
    @lazyInject(CoreDI.IEventManagerWithAsync)
    protected eventManager!: IEventManagerWithAsync;

    @lazyInject(CoreDI.ScreenManager)
    protected screenManager!: ScreenManager;

    private offResizeEffect?: () => void;
    private _mounted = false;

    constructor(props: AlignerByScreenProps) {
        super(props);
        const {width, height} = this.screenManager.getScreenState();

        this.state = {
            screenWidth: width,
            screenHeight: height
        };
    }

    public componentDidMount(): void {
        this._mounted = true;

        if (slotStore.isV2SafeZoneSystemActive.value) {
            this.offResizeEffect = Store.effect(() => {
                const safeZone = slotStore.stage_safeZoneState_ui.value;
                const screenBounds = safeZone.calcInSafeFader();

                if (this._mounted) {
                    this.setState({
                        screenWidth: screenBounds.width * 2,
                        screenHeight: screenBounds.height * 2
                    });
                }
            });
        } else {
            const {width, height} = this.screenManager.getScreenState();

            this.setState({screenWidth: width, screenHeight: height});
            this.eventManager.on('General.Screen.RESIZED', this.onResized.bind(this), this);
        }
    }

    public componentWillUnmount(): void {
        this._mounted = false;

        if (this.offResizeEffect) {
            this.offResizeEffect();
            this.offResizeEffect = undefined;
        }

        this.eventManager.off('General.Screen.RESIZED', this);
    }

    protected onResized({width, height}: ScreenParameters): void {
        if (!slotStore.isV2SafeZoneSystemActive.value && this._mounted) {
            this.setState({ screenWidth: width, screenHeight: height });
        }
    }

    public render(): JSX.Element {
        const {screenWidth, screenHeight} = this.state;
        const {horizontalAlign = 'left', verticalAlign = 'top'} = this.props;
        const alignItems = VERTICAL_ALIGN[verticalAlign];
        const justifyContent = HORIZONTAL_ALIGN[horizontalAlign];

        return (
            <FlexBoxComponent
                name={'AlignerByScreen'}
                alignItems={alignItems}
                justifyContent={justifyContent}
                containerWidth={screenWidth}
                containerHeight={screenHeight}
            >
                {this.props.children}
            </FlexBoxComponent>
        );
    }
}
